/**
 * What `GET /activity/stops/:id` answers (`server/src/services/activity/stop.ts`),
 * as the phone's stop page reads it (Board 14 A).
 */
import type { Recurrence } from '@rowted/shared'

export interface StopPageCard {
  id: string
  kind: 'appointment' | 'meeting'
  tags: string[]
  title: string
  date: string
  start: number
  end: number
  personId: string | null
  personName: string | null
  people: { id: string; name: string }[]
  address: string | null
  line1: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  propertyName: string | null
  orgUnitId: string | null
  latitude: number | null
  longitude: number | null
  remote: boolean
  drives: string | null
  driveIn: { minutes: number; from: string; miles: number } | null
  contact: {
    id: string; name: string; firstName: string; phone: string | null; phoneKind: string | null
    email: string | null; prefers: 'sms' | 'email' | null; optedOut: boolean
  } | null
  job: { id: string; reference: string; title: string; status: string; division: string | null; wanted: string | null } | null
  wants: string | null
  access: string | null
  propertyId: string | null
  startsIn: number | null
  endsIn: number | null
  conversationId: string | null
  latest: {
    who: string; at: string; channel: 'sms' | 'email'; direction: 'inbound' | 'outbound'; preview: string
  } | null
  done: boolean
  confirmed: boolean
  editable: boolean
  /** How it repeats (0138), or null — a repeating meeting's *Edit* asks *Just this one · Every one*. */
  recurrence?: Recurrence | null
  /** The occurrence the page is about: its own day in the series, moved or not (0141). */
  occurrence?: string | null
}

/** *in 19 min* before, *now* during, *<n> min ago* after (§10.3) — and nothing on another day. */
export function whenWords(card: Pick<StopPageCard, 'startsIn' | 'endsIn'>): string | null {
  const { startsIn, endsIn } = card
  if (startsIn === null || endsIn === null) return null
  const span = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest ? `${hours} h ${rest} min` : `${hours} h`
  }
  if (startsIn > 0) return startsIn < 12 * 60 ? `in ${span(startsIn)}` : null
  if (endsIn > 0) return 'now'
  return -endsIn < 12 * 60 ? `${span(-endsIn)} ago` : null
}

/** *(717) 555-0193* — ten digits as the boards print them; anything else as it was typed. */
export function phoneWords(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (ten.length !== 10) return phone
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}
